import { action, computed, observable } from "mobx";

import { delay } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import { NotFoundError } from "@bps/http-client";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import { Entity } from "@libs/api/hub/Entity.ts";
import { EntityEventData } from "@libs/api/hub/EntityEventData.ts";
import { EventAction } from "@libs/api/hub/EventAction.ts";
import {
  AddStatementBulkDto,
  GetStatementArgs
} from "@libs/gateways/billing/BillingGateway.dtos.ts";
import { DocumentUrlDto } from "@libs/gateways/clinical/ClinicalGateway.dtos";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { StatementGenerateFormValues } from "@modules/billing/screens/statement/components/StatementGenerateModal.type.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { Statement } from "@stores/billing/models/Statement.ts";
import { Contact } from "@stores/practice/models/Contact.ts";

export interface StatementItem {
  statement: Statement;
  contact: Contact;
}

export class StatementItemsHelper {
  constructor(private root: IRootStore) {
    this.root.billing.hub.onEntityEvent(
      Entity.Statement,
      this.printStatementsEvent
    );
  }

  private printStatementsEvent = async (message: EntityEventData) => {
    if (
      this.printStatements &&
      message.entity === Entity.Statement &&
      message.action === EventAction.Command &&
      message.id === "Print"
    ) {
      const statementsToPrint: string[] = message.key.split(":");
      const docs = await Promise.all(
        statementsToPrint.map(async sp => {
          const statement = await this.root.billing.getStatement(sp, {
            ignoreCache: true
          });
          if (statement) {
            return await this.getStatementUrl(statement);
          }
          return;
        })
      );
      if (docs.length > 0) {
        const docUrls = docs
          .filter((d): d is DocumentUrlDto => d !== undefined)
          .map(d => d.url);

        const response = await this.root.document.mergePdf(docUrls);
        this.root.billing.ui.setStatementPrintDocumentData({
          content: response.content,
          contentType: response.contentType
        });
        this.setPrintStatements(false);
      }
    }
  };

  private async getStatementUrl(statement: Statement): Promise<DocumentUrlDto> {
    try {
      const doc = await this.root.billing.getBillingStoredDocumentUrl(
        {
          statementId: statement.id,
          contactId: statement.accountContactId
        },
        true
      );
      return doc;
    } catch (error) {
      if (error instanceof NotFoundError) {
        //URL has not been created yet
        await delay(500);
        return await this.getStatementUrl(statement);
      }
      throw error;
    }
  }

  @observable
  printStatements = false;

  @action
  setPrintStatements = (val: boolean) => {
    this.printStatements = val;
  };

  @observable
  emailedStatementsNumber = 0;

  @action
  setEmailedStatementsNumber = (val: number) => {
    this.emailedStatementsNumber = val;
  };

  @observable
  selectedIds: string[] | undefined = [];

  @action
  setSelectedIds = (selected: string[]) => {
    this.selectedIds = selected;
  };

  @computed
  public get numberOfPrinted(): number {
    return (this.selectedIds?.length ?? 0) - this.emailedStatementsNumber;
  }

  getStatements = async (query: GetStatementArgs) => {
    return this.root.billing.getStatements({
      ...query,
      accountContactId: undefined
    });
  };

  handleSubmitAction = async (values: StatementGenerateFormValues) => {
    const req: AddStatementBulkDto = {
      date: DateTime.now().toISODate(),
      startDate: values.endDate
        ? DateTime.fromJSDate(values.endDate)
            .minus({ days: values.gapDays })
            .toISODate()
        : DateTime.now().plus({ days: 1 }).toISODate(),
      endDate: values.endDate
        ? DateTime.fromJSDate(values.endDate).toISODate()
        : DateTime.now().plus({ days: 2 }).toISODate(),
      owingOver: values.valueFilter ? values.owingOver : undefined,
      creditOver: values.valueFilter ? values.creditOver : undefined,
      accountContactIds: this.selectedIds
    };
    this.setPrintStatements(this.numberOfPrinted > 0);
    return this.root.billing.addStatementBulk(req);
  };

  @observable total: number | undefined;

  @action setTotal = (value: number | undefined) => {
    this.total = value;
  };

  contactTypesToSend = (values: StatementGenerateFormValues): string[] => {
    if (values.contactType === ContactType.Patient)
      return [ContactType.Patient, ContactType.Individual];

    if (values.contactType === ContactType.Organisation)
      return [ContactType.Organisation];

    return [];
  };

  searchAccountIds = async (
    values: StatementGenerateFormValues,
    query?: PagingOptions
  ) => {
    const { endDate, owingOver, creditOver, gapDays } = values;

    const res = await this.root.billing.getAccountIds({
      ...query,
      startDate: endDate
        ? DateTime.fromJSDate(endDate).minus({ days: gapDays }).toISODate()
        : DateTime.now().plus({ days: 1 }).toISODate(),
      endDate: endDate
        ? DateTime.fromJSDate(endDate).toISODate()
        : DateTime.now().plus({ days: 2 }).toISODate(),
      owingOver: owingOver && owingOver > 0 ? owingOver : undefined,
      creditOver: creditOver && creditOver > 0 ? creditOver : undefined,
      contactTypes: this.contactTypesToSend(values),
      take: 1000,
      total: true
    });

    this.setTotal(res.total);

    return res;
  };

  statementGenerateFormNameOf = nameOfFactory<StatementGenerateFormValues>();

  @observable
  isStatementNewModalVisible = false;
  @action
  setIsStatementNewModalVisible = (value: boolean) => {
    this.isStatementNewModalVisible = value;
  };
}
